'use client';

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
} from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  CheckCircle2,
  Loader2,
  X,
  AlertCircle,
  ChevronUp,
  ChevronDown,
  Clock,
  Pause,
  AlertTriangle,
} from 'lucide-react';
import { backgroundTaskService } from '@/lib/services/background-tasks';
import { Task } from '@/lib/types';

interface TaskbarContextType {
  tasks: Task[];
  addTask: (task: Omit<Task, 'id' | 'createdAt'>) => Promise<string>;
  updateTask: (id: string, updates: Partial<Task>) => void;
  removeTask: (id: string) => void;
  clearCompleted: () => void;
}

const TaskbarContext = createContext<TaskbarContextType | undefined>(undefined);

export const useTaskbar = () => {
  const context = useContext(TaskbarContext);
  if (!context) {
    throw new Error('useTaskbar must be used within a TaskbarProvider');
  }
  return context;
};

// Taskbar Provider
export const TaskbarProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [tasks, setTasks] = useState<Task[]>([]);

  // Sync with background task service
  useEffect(() => {
    const updateTasks = () => {
      setTasks(backgroundTaskService.getAllTasks());
    };

    // Initial load
    updateTasks();

    // Set up interval to sync tasks
    const interval = setInterval(updateTasks, 100);

    return () => clearInterval(interval);
  }, []);

  const addTask = useCallback((taskData: Omit<Task, 'id' | 'createdAt'>) => {
    return backgroundTaskService.addTask(taskData);
  }, []);

  const updateTask = useCallback((id: string, updates: Partial<Task>) => {
    backgroundTaskService.updateTask(id, updates);
  }, []);

  const removeTask = useCallback((id: string) => {
    backgroundTaskService.removeTask(id);
  }, []);

  const clearCompleted = useCallback(() => {
    backgroundTaskService.clearCompleted();
  }, []);

  return (
    <TaskbarContext.Provider
      value={{ tasks, addTask, updateTask, removeTask, clearCompleted }}
    >
      {children}
      <Taskbar />
    </TaskbarContext.Provider>
  );
};

// Individual Task Item
const TaskItem: React.FC<{ task: Task; onRemove: (id: string) => void }> = ({
  task,
  onRemove,
}) => {
  const getStatusIcon = () => {
    switch (task.status) {
      case 'pending':
        return <Clock className="h-4 w-4 text-muted-foreground" />;
      case 'running':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      case 'paused':
        return <Pause className="h-4 w-4 text-yellow-500" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusColor = () => {
    switch (task.status) {
      case 'pending':
        return 'bg-muted text-muted-foreground';
      case 'running':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'completed':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'failed':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatTime = (date: Date) => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className="flex items-start gap-3 p-3 bg-background border rounded-lg shadow-sm hover:shadow-md transition-shadow">
      <div className="flex-shrink-0 mt-0.5">{getStatusIcon()}</div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h4 className="text-sm font-medium text-foreground truncate">
              {task.title}
            </h4>
            {task.description && (
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                {task.description}
              </p>
            )}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="h-6 w-6 p-0 flex-shrink-0"
            onClick={() => onRemove(task.id)}
          >
            <X className="h-3 w-3" />
          </Button>
        </div>

        <div className="flex items-center gap-2 mt-2">
          <Badge
            variant="secondary"
            className={cn('text-xs', getStatusColor())}
          >
            {task.status.charAt(0).toUpperCase() + task.status.slice(1)}
          </Badge>
          <span className="text-xs text-muted-foreground">
            {formatTime(task.createdAt)}
          </span>
        </div>

        {task.progress !== undefined && task.status === 'running' && (
          <div className="mt-2">
            <div className="w-full bg-muted rounded-full h-1.5">
              <div
                className="bg-blue-500 h-1.5 rounded-full transition-all duration-300"
                style={{ width: `${task.progress}%` }}
              />
            </div>
            <span className="text-xs text-muted-foreground mt-1">
              {Math.round(task.progress)}%
            </span>
          </div>
        )}

        {task.error && (
          <div className="mt-2 p-2 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300">
            {task.error}
          </div>
        )}

        {task.actions && task.actions.length > 0 && (
          <div className="flex gap-1 mt-2">
            {task.actions.map((action) => (
              <Button
                key={action.id}
                variant={action.variant || 'outline'}
                size="sm"
                className="h-6 text-xs"
                onClick={action.onClick}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Main Taskbar Component
const Taskbar: React.FC = () => {
  const { tasks, removeTask, clearCompleted } = useTaskbar();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isVisible, setIsVisible] = useState(false);

  // Show taskbar when there are tasks
  React.useEffect(() => {
    setIsVisible(tasks.length > 0);
  }, [tasks.length]);

  if (!isVisible) return null;

  const runningTasks = tasks.filter((task) => task.status === 'running');
  const completedTasks = tasks.filter((task) => task.status === 'completed');
  const otherTasks = tasks.filter(
    (task) => !['running', 'completed'].includes(task.status),
  );

  const displayTasks = tasks;

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-sm w-full">
      <div className="bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 border rounded-lg shadow-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-3 border-b">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-medium">Background Tasks</h3>
            <Badge variant="secondary" className="text-xs">
              {tasks.length}
            </Badge>
          </div>
          <div className="flex items-center gap-1">
            {completedTasks.length > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-xs"
                onClick={clearCompleted}
              >
                Clear
              </Button>
            )}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0 hover:bg-accent hover:scale-110 transition-all duration-200"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsExpanded(!isExpanded);
              }}
              aria-label={isExpanded ? 'Minimize' : 'Maximize'}
              title={isExpanded ? 'Minimize' : 'Maximize'}
            >
              {isExpanded ? (
                <ChevronDown className="h-3 w-3" />
              ) : (
                <ChevronUp className="h-3 w-3" />
              )}
            </Button>
          </div>
        </div>

        {/* Task List */}
        {isExpanded && (
          <div className="max-h-96 overflow-y-auto">
            {displayTasks.length === 0 ? (
              <div className="p-4 text-center text-sm text-muted-foreground">
                No active tasks
              </div>
            ) : (
              <div className="p-3 space-y-2">
                {displayTasks.map((task) => (
                  <TaskItem key={task.id} task={task} onRemove={removeTask} />
                ))}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        {!isExpanded && (
          <div className="p-2 border-t bg-muted/50">
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-6 text-xs"
              onClick={() => setIsExpanded(true)}
            >
              Show {tasks.length} {tasks.length === 1 ? 'task' : 'tasks'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};
